import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  clampScale,
  isTextElement,
  resizeTextBox,
  transformStyle,
} from './mediaCreatorElementLayout';
import {
  elementLayoutKeyFromElement,
  findSelectableElement,
  resolveElementByKey,
} from './mediaCreatorDomRegistry';
import { unzoomRect, ZOOM_ATTR } from './mediaCreatorPhotoFit';

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
/** The logo keeps its aspect ratio by default (Shift stretches freely); other elements are the reverse. */
const LOGO_KEY = 'logo:brand';

function isTypingInHost(host, active) {
  if (!active || !host) return false;
  return host.contains(active) && active.getAttribute?.('contenteditable') === 'true';
}

/** Do not steal Delete/Backspace from properties panel, title, or other inputs. */
function isFormFieldFocused(active) {
  if (!active || typeof active !== 'object') return false;
  const tag = active.tagName?.toLowerCase?.();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (active.isContentEditable || active.getAttribute?.('contenteditable') === 'true') return true;
  return Boolean(active.closest?.('.mc-props, .mc-editor-bar, .mc-title-input, .mc-ai-refine'));
}

/** On-screen box of an element; zoomed slot images report their frame, not the zoomed content. */
function frameRect(el) {
  const box = el.getBoundingClientRect();
  return el.hasAttribute(ZOOM_ATTR) ? unzoomRect(box, el) : box;
}

function findSelectable(target, host) {
  return findSelectableElement(target, host);
}

const MediaCreatorHtmlSelectionOverlay = ({
  hostRef,
  scale = 1,
  selectedKey,
  onSelectKey,
  elementLayout = {},
  onElementLayoutChange,
  onRequestDelete,
}) => {
  const [rect, setRect] = useState(null);
  const dragRef = useRef(null);

  const selectedEl = useCallback(() => {
    const host = hostRef.current;
    if (!host || !selectedKey) return null;
    return resolveElementByKey(host, selectedKey);
  }, [hostRef, selectedKey]);

  const measure = useCallback(() => {
    const el = selectedEl();
    const host = hostRef.current;
    if (!el || !host) {
      setRect(null);
      return;
    }
    const hostBox = host.getBoundingClientRect();
    const box = frameRect(el);
    setRect({
      left: (box.left - hostBox.left) / scale,
      top: (box.top - hostBox.top) / scale,
      width: box.width / scale,
      height: box.height / scale,
    });
  }, [hostRef, scale, selectedEl]);

  useEffect(() => {
    measure();
    const host = hostRef.current;
    if (!host) return undefined;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(host);
    window.addEventListener('scroll', measure, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', measure, true);
    };
  }, [hostRef, measure, selectedKey, elementLayout]);

  useEffect(() => {
    const onKey = (e) => {
      if (!selectedKey) return;
      const host = hostRef.current;
      const active = document.activeElement;
      if (isTypingInHost(host, active) || isFormFieldFocused(active)) return;
      if (e.key === 'Escape') {
        onSelectKey?.(null);
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        onRequestDelete?.(selectedKey);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hostRef, onRequestDelete, onSelectKey, selectedKey]);

  const patchTransform = useCallback(
    (key, patch) => {
      if (!key || !onElementLayoutChange) return;
      const prev = elementLayout[key] || { tx: 0, ty: 0, sx: 1, sy: 1 };
      onElementLayoutChange(key, { ...prev, ...patch });
    },
    [elementLayout, onElementLayoutChange],
  );

  const startDrag = (mode, handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = selectedEl();
    if (!el || !selectedKey) return;
    const key = selectedKey;
    const startX = e.clientX;
    const startY = e.clientY;
    const base = elementLayout[key] || { tx: 0, ty: 0, sx: 1, sy: 1 };
    const startTx = Number(base.tx) || 0;
    const startTy = Number(base.ty) || 0;
    const startSx = Number(base.sx) || 1;
    const startSy = Number(base.sy) || 1;
    const box = frameRect(el);
    const startW = box.width / scale;
    const startH = box.height / scale;
    const text = isTextElement(el);

    dragRef.current = { mode, handle, key };

    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / scale;
      const dy = (ev.clientY - startY) / scale;
      if (mode === 'move') {
        patchTransform(key, { tx: startTx + dx, ty: startTy + dy });
        return;
      }
      if (text) {
        const next = resizeTextBox({ handle, startTx, startTy, startW, startH, dx, dy });
        patchTransform(key, { ...next, sx: 1, sy: 1 });
        return;
      }
      const h = handle || 'se';
      const w0 = Math.max(startW, 1);
      const h0 = Math.max(startH, 1);
      let rx = 1;
      let ry = 1;
      if (h.includes('e')) rx = (w0 + dx) / w0;
      if (h.includes('w')) rx = (w0 - dx) / w0;
      if (h.includes('s')) ry = (h0 + dy) / h0;
      if (h.includes('n')) ry = (h0 - dy) / h0;
      const keepAspect = key === LOGO_KEY ? !ev.shiftKey : ev.shiftKey;
      if (keepAspect) {
        const horizontal = h.includes('e') || h.includes('w');
        const vertical = h.includes('n') || h.includes('s');
        let r;
        if (horizontal && vertical) r = Math.abs(rx - 1) >= Math.abs(ry - 1) ? rx : ry;
        else r = horizontal ? rx : ry;
        rx = r;
        ry = r;
      }
      const sx = clampScale(startSx * rx);
      const sy = clampScale(startSy * ry);
      const patch = { sx, sy };
      if (h.includes('w')) patch.tx = startTx + w0 - (w0 * sx) / startSx;
      if (h.includes('n')) patch.ty = startTy + h0 - (h0 * sy) / startSy;
      patchTransform(key, patch);
    };

    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      measure();
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (!rect || !selectedKey) return null;

  return (
    <div className="mc-html-selection-layer" aria-hidden>
      <div
        className="mc-html-selection-box"
        style={{
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        }}
        onMouseDown={(e) => startDrag('move', null, e)}
      />
      {HANDLES.map((h) => (
        <div
          key={h}
          className={`mc-html-selection-handle mc-html-selection-handle-${h}`}
          style={{
            left: rect.left + (h.includes('w') ? 0 : h.includes('e') ? rect.width : rect.width / 2),
            top: rect.top + (h.includes('n') ? 0 : h.includes('s') ? rect.height : rect.height / 2),
          }}
          onMouseDown={(e) => startDrag('resize', h, e)}
        />
      ))}
    </div>
  );
};

export { findSelectable, isTypingInHost, transformStyle };
export { elementLayoutKeyFromElement as elementLayoutKey } from './mediaCreatorDomRegistry';
export default MediaCreatorHtmlSelectionOverlay;
